import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Cart-drawer Recharge dynamic-bundle module (feat/lg-cart-bundle-grouping).
 *
 * Two responsibilities, both document-delegated (the controls live INSIDE the
 * drawer's keyed morph target and are re-created on every mutation, §4.5 / §1.25
 * — a script or a bound reference inside the fragment is inert):
 *
 * 1. REMOVE BUNDLE — an atomic multi-key removal. One `/cart/update.js` with
 *    `{updates:{<key>:0,…}}` zeroes every child line of the bundle in a single
 *    request + single morph (clone of the live-verified mutation shape in
 *    assets/lg-cart-samples.js; §4.14 accepts line KEYS, §5.16 one atomic update
 *    per removal). Resolves the dispatched `CartLinesUpdateEvent` WITH
 *    `detail.sections` so cart-items-component performs the one hydration morph
 *    (§4.15); on failure it rejects (like the samples guard) so the header
 *    bubble is not zeroed while the lines are still in the cart. `action:'remove'`
 *    keeps the §4.7 auto-open gate cold. Focus restoration after the morph deletes
 *    the button is delegated to assets/lg-cart-announce.js over the
 *    `lg:cart:restore-focus` seam (§4.59; orphan-guarded, §4.14).
 *
 * 2. Header-bubble count corrector — "a bundle counts as 1" (owner decision
 *    2026-09-08; §4.47). cart-icon.js writes the bubble from the CDN
 *    `cart.totalQuantity` (RAW item_count, not vendorable, no properties in the
 *    event cart), so it over-counts a bundle by (children − 1). This corrector
 *    runs AFTER cart-icon's own write (listener registered later + a microtask
 *    hop) and re-writes the corrected total through cart-icon's public
 *    `renderCartBubble(n, false)` — which also re-stamps sessionStorage, fixing
 *    the bfcache/`ensureCartBubbleIsCorrect` seam (cart-icon.js:105-133). The
 *    corrected number is read from the server-rendered `data-lg-display-count`
 *    carrier inside `detail.sections` (single source: snippets/lg-cart-display-count.liquid),
 *    falling back to a recompute from `detail.items` (which carries raw
 *    properties). The drawer header + bubble initial paint are already corrected
 *    in Liquid; this only patches the JS-owned bubble write.
 */

const DISABLED_CLASS = 'cart-items-disabled';

/**
 * Collects the SRA section ids to re-render (same walk as
 * component-cart-items.js updateQuantity / lg-cart-samples.js).
 * @returns {string}
 */
function sectionsParam() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids).join(',');
}

/**
 * Atomically removes every child line of a bundle in one cart request.
 * @param {HTMLElement} button - the clicked `[data-lg-bundle-remove]` control.
 */
async function removeBundle(button) {
  const keys = (button.dataset.lgBundleKeys || '').split(',').filter(Boolean);
  if (!keys.length) {
    // Clear the announce latch set on press (lg-cart-announce.js, §4.59). Without this the stale
    // pending is consumed by the NEXT unrelated cartLinesUpdate and announces "… bundle
    // removed from your bag" for a mutation that removed nothing.
    document.dispatchEvent(new CustomEvent('lg:cart:announce-cancel'));
    return;
  }

  // Re-entry guard — a keyboard second-fire (Space/Enter) bypasses the CSS
  // pointer-events block, so no-op while any flight holds the class (mirrors
  // lg-cart-subscribe.js round-4 guard).
  const host = button.closest('cart-items-component');
  if (host instanceof HTMLElement && host.classList.contains(DISABLED_CLASS)) {
    document.dispatchEvent(new CustomEvent('lg:cart:announce-cancel'));
    return;
  }
  host?.classList.add(DISABLED_CLASS);
  host?.setAttribute('aria-busy', 'true');
  button.disabled = true;

  // Optimistic group-removal animation (reused .removing keyframe, reduced-motion
  // guarded in CSS). Reverted on the error path since no morph will replace it.
  const wrapper = button.closest('[data-lg-bundle]');
  if (wrapper instanceof HTMLElement) {
    wrapper.style.setProperty('--row-height', `${wrapper.offsetHeight}px`);
    wrapper.classList.add('removing');
  }

  const sections = sectionsParam();
  const deferred = CartLinesUpdateEvent.createPromise();
  // action 'remove' — cart-drawer.js only auto-opens on 'add', so a closed
  // drawer stays closed while the bubble updates.
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'remove',
      context: 'cart',
      lines: keys.map((id) => ({ id, quantity: 0 })),
      promise: deferred.promise,
    })
  );

  try {
    const updates = Object.fromEntries(keys.map((key) => [key, 0]));
    const body = JSON.stringify({
      updates,
      sections,
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
    const data = await response.json();

    // Discriminate success from a JSON-bodied error BEFORE resolving. /cart/update.js
    // answers a stale or unknown line key with 422 + a JSON body, so response.json()
    // resolves and nothing throws — the old code took the success path on an error and
    // did exactly what the catch block below warns against: the optimistic `.removing`
    // collapse was never reverted (the bundle vanished while its lines were still in the
    // cart) and createCartFromAjaxResponse got the error payload, leaving totalQuantity
    // undefined so cart-icon fell through to `?? 0` and ZEROED the header bubble.
    // The vendor discriminates the same way (component-cart-items.js:253).
    // This is also the plan's "stale-key → error-resolve fallback" (§4.14).
    if (!response.ok || data.status || data.errors) {
      throw new Error(data.description || data.message || data.errors || 'cart update failed');
    }

    // Resolve WITH detail.sections so cart-items-component owns the single morph
    // (§4.15). We do NOT morph here.
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
      detail: {
        sections: data.sections,
        items: data.items,
        itemCount: data.item_count,
        source: 'lg-cart-bundle',
        didError: false,
      },
    });

    // Focus restore is owned by assets/lg-cart-announce.js (focus-and-announce contract,
    // horizon-mechanics §4.59): the morph deletes the REMOVE BUNDLE button the user just
    // activated, and that module already runs the orphan-guarded, re-arming restore for
    // every other cart mutation. Handing the removed keys over the event mesh keeps ONE
    // implementation instead of the near-copy that used to live here.
    document.dispatchEvent(new CustomEvent('lg:cart:restore-focus', { detail: { removedKeys: keys } }));
  } catch (error) {
    // Revert the optimistic animation — no morph is coming to replace the rows.
    if (wrapper instanceof HTMLElement) {
      wrapper.classList.remove('removing');
      wrapper.style.removeProperty('--row-height');
    }
    // Reject (do NOT resolve {cart:null}) — like lg-cart-samples.js:106, resolving
    // would zero the header bubble while the bundle is still in the cart.
    deferred.reject(error);
    console.error('lg-cart-bundle: failed to remove bundle', error);
  } finally {
    button.disabled = false;
    host?.classList.remove(DISABLED_CLASS);
    host?.removeAttribute('aria-busy');
  }
}

// --- Bubble count corrector ------------------------------------------------

/**
 * Derives the corrected "bundle = 1" display count from a resolved cart event.
 * Prefers the server-rendered carrier in `detail.sections`; falls back to a
 * recompute from `detail.items` (raw line properties). Returns null when neither
 * is available (leave the bubble untouched rather than clobber it).
 * @param {{ sections?: Record<string, string>, items?: any[] } | undefined} detail
 * @returns {number | null}
 */
function correctedDisplayCount(detail) {
  const sectionsMap = detail?.sections;
  if (sectionsMap) {
    for (const html of Object.values(sectionsMap)) {
      if (typeof html !== 'string') continue;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const el = doc.querySelector('[data-lg-display-count]');
      if (el) {
        const n = parseInt(el.getAttribute('data-lg-display-count') ?? '', 10);
        if (Number.isFinite(n)) return n;
      }
    }
  }

  const items = detail?.items;
  if (Array.isArray(items)) {
    const seen = new Set();
    let count = 0;
    for (const item of items) {
      const bundle = item?.properties?._rc_bundle;
      if (bundle) {
        if (!seen.has(bundle)) {
          seen.add(bundle);
          count += 1;
        }
      } else {
        count += item?.quantity ?? 0;
      }
    }
    return count;
  }

  return null;
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const promise = /** @type {CartLinesUpdateEvent} */ (event).promise;
  promise
    ?.then(({ detail }) => {
      const corrected = correctedDisplayCount(detail);
      if (corrected === null) return;
      const cartIcon = document.querySelector('cart-icon');
      if (!cartIcon || typeof (/** @type {any} */ (cartIcon).renderCartBubble) !== 'function') return;
      // Run AFTER cart-icon's own raw write (its listener resolves first —
      // registered at header load; the microtask hop guarantees ordering even if
      // that ever changes). animate:false so the correction doesn't re-pulse.
      queueMicrotask(() => {
        /** @type {any} */ (cartIcon).renderCartBubble(corrected, false);
      });
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[lg-cart-bundle] count corrector: event promise rejected', error);
    });
});

// --- Delegated removal listener (document-level; morph-replaced controls) ---

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const button = target.closest('[data-lg-bundle-remove]');
  if (!(button instanceof HTMLElement)) return;
  event.preventDefault();
  removeBundle(button);
});
